import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GarageActions } from './garage.actions';
import { CantieriActions } from '../cantieri/cantieri.actions';
import { GaragePort } from '../../core/services/garage/garage.port';
import { LoadingService } from '../../core/services/loading/loading.service';
import { catchError, filter, map, of, switchMap, tap, finalize, withLatestFrom } from 'rxjs';
import { SwalService } from '../../core/services/alerts/swal.service';
import { selectLastQuery, selectPageSize, selectSelected } from './garage.selectors';

@Injectable()
export class GarageEffects {
  private actions$ = inject(Actions);
  private api = inject(GaragePort);
  private store = inject(Store);
  private loading = inject(LoadingService);
  private swalService = inject(SwalService);

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.loadList),
      withLatestFrom(this.store.select(selectPageSize)),
      switchMap(([{ params }, pageSize]) => {
        const queryParams = {
          ...params,
          size: params?.size ?? pageSize,
          page: params?.page ?? 0,
        };
        return this.api.getList(queryParams).pipe(
          map((result) => {
            if (result.notModified) {
              return GarageActions.loadListNotModified();
            }

            return GarageActions.loadListSuccess({
              items: result.response.content,
              totalElements: result.response.totalElements,
              totalPages: result.response.totalPages,
              pageIndex: result.response.pageIndex,
            });
          }),
          catchError(err =>
            of(GarageActions.loadListFailure({ error: err?.message ?? 'Errore caricamento' }))
          ),
        );
      })
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.loadDetails),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map(item => GarageActions.loadDetailsSuccess({ item })),
          catchError(err =>
            of(GarageActions.loadDetailsFailure({ error: err?.message ?? 'Errore dettagli' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.create),
      tap(() => this.loading.show()),
      switchMap(({ dto, scheda }) =>
        this.api.create(dto, scheda).pipe(
          map(id => GarageActions.createSuccess({ id })),
          catchError(err =>
            of(GarageActions.createFailure({ error: err?.message ?? 'Errore creazione' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.update),
      tap(() => this.loading.show()),
      switchMap(({ id, dto }) =>
        this.api.update(id, dto).pipe(
          map(resId => GarageActions.updateSuccess({ id: resId })),
          catchError(err =>
            of(GarageActions.updateFailure({ error: err?.message ?? 'Errore aggiornamento' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  addManutenzione$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.addManutenzione),
      tap(() => this.loading.show()),
      switchMap(({ dto }) =>
        this.api.addManutenzione(dto).pipe(
          map(id => GarageActions.addManutenzioneSuccess({ id })),
          catchError(err =>
            of(GarageActions.addManutenzioneFailure({ error: err?.message ?? 'Errore manutenzione' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  associateToCantiere$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.associateToCantiere),
      tap(() => this.loading.show()),
      switchMap(({ id, idCantiere }) =>
        this.api.associateToCantiere(id, idCantiere).pipe(
          map(resId => GarageActions.associateToCantiereSuccess({ id: Number(resId ?? id), idCantiere })),
          catchError(err =>
            of(GarageActions.associateToCantiereFailure({ error: err?.message ?? 'Errore associazione cantiere' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  endCantiereAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.endCantiereAssociation),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.endCantiereAssociation(id).pipe(
          map(resId => GarageActions.endCantiereAssociationSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(GarageActions.endCantiereAssociationFailure({ error: err?.message ?? 'Errore rimozione associazione cantiere' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  loadStoricoManutenzioni$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.loadStoricoManutenzioni),
      switchMap(({ params }) =>
        this.api.getStoricoManutenzioni(params).pipe(
          map(response =>
            GarageActions.loadStoricoManutenzioniSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError(err => {
            if (err?.status === 403) {
              return of(
                GarageActions.loadStoricoManutenzioniSuccess({
                  items: [],
                  totalElements: 0,
                  totalPages: 0,
                  pageIndex: 0,
                })
              );
            }
            return of(GarageActions.loadStoricoManutenzioniFailure({ error: err?.message ?? 'Errore caricamento storico manutenzioni' }));
          }),
        )
      )
    )
  );

  loadStoricoAssociazioni$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.loadStoricoAssociazioni),
      switchMap(({ params }) =>
        this.api.getStoricoAssociazioni(params).pipe(
          map(response =>
            GarageActions.loadStoricoAssociazioniSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError(err => {
            if (err?.status === 403) {
              return of(
                GarageActions.loadStoricoAssociazioniSuccess({
                  items: [],
                  totalElements: 0,
                  totalPages: 0,
                  pageIndex: 0,
                })
              );
            }
            return of(GarageActions.loadStoricoAssociazioniFailure({ error: err?.message ?? 'Errore caricamento storico associazioni' }));
          }),
        )
      )
    )
  );

  loadStoricoManutenzioniAfterDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.loadDetailsSuccess),
      map(({ item }) =>
        GarageActions.loadStoricoManutenzioni({
          params: { idMezzo: item.id, size: 20, page: 0 },
        })
      )
    )
  );

  loadStoricoAssociazioniAfterDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.loadDetailsSuccess),
      map(({ item }) =>
        GarageActions.loadStoricoAssociazioni({
          params: { idMezzo: item.id, size: 20, page: 0 },
        })
      )
    )
  );

  reloadAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.createSuccess),
      withLatestFrom(this.store.select(selectLastQuery)),
      map(([_, lastQuery]) => GarageActions.loadList({ params: lastQuery }))
    ),
  );

  refreshDetailsAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GarageActions.updateSuccess,
        GarageActions.associateToCantiereSuccess,
        GarageActions.endCantiereAssociationSuccess,
        GarageActions.addManutenzioneSuccess,
        GarageActions.associateToDipendenteSuccess,
        GarageActions.endDipendenteAssociationSuccess,
        GarageActions.setUsabilitySuccess,
      ),
      withLatestFrom(this.store.select(selectSelected)),
      filter(([_, selected]) => !!selected?.id),
      map(([_, selected]) => GarageActions.loadDetails({ id: selected!.id }))
    )
  );

  refreshListAfterCantiereEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.endSuccess),
      withLatestFrom(this.store.select(selectLastQuery)),
      map(([_, lastQuery]) => GarageActions.loadList({ params: lastQuery }))
    )
  );

  refreshSelectedAfterCantiereEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.endSuccess),
      withLatestFrom(this.store.select(selectSelected)),
      filter(([{ id }, selected]) => !!selected?.id && Number(selected.idCantiere) === Number(id)),
      map(([_, selected]) => GarageActions.loadDetails({ id: selected!.id }))
    )
  );

  showMutationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          GarageActions.createSuccess,
          GarageActions.updateSuccess,
          GarageActions.associateToCantiereSuccess,
          GarageActions.endCantiereAssociationSuccess,
          GarageActions.addManutenzioneSuccess,
          GarageActions.endDipendenteAssociationSuccess,
          GarageActions.setUsabilitySuccess
        ),
        tap((action) => {
          if (action.type === GarageActions.createSuccess.type) this.swalService.success('Mezzo creato con successo!');
          else if (action.type === GarageActions.updateSuccess.type) this.swalService.success('Mezzo aggiornato con successo!');
          else if (action.type === GarageActions.associateToCantiereSuccess.type) this.swalService.success('Mezzo associato al cantiere!');
          else if (action.type === GarageActions.endCantiereAssociationSuccess.type) this.swalService.success('Associazione cantiere terminata');
          else if (action.type === GarageActions.endDipendenteAssociationSuccess.type) this.swalService.success('Associazione dipendente terminata');
          else if (action.type === GarageActions.setUsabilitySuccess.type) this.swalService.success('Stato mezzo aggiornato!');

          else this.swalService.success('Manutenzione aggiunta con successo!');
        })
      ),
    { dispatch: false }
  );

  showMutationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          GarageActions.createFailure,
          GarageActions.updateFailure,
          GarageActions.associateToCantiereFailure,
          GarageActions.endCantiereAssociationFailure,
          GarageActions.addManutenzioneFailure,
          GarageActions.setUsabilityFailure,
        ),
        tap(({ error }) => this.swalService.error(`Errore: ${error}`))
      ),
    { dispatch: false }
  );

  showLoadFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          GarageActions.loadListFailure,
          GarageActions.loadDetailsFailure,
          GarageActions.loadStoricoManutenzioniFailure,
          GarageActions.loadStoricoAssociazioniFailure,
          GarageActions.endDipendenteAssociationFailure,
        ),
        tap(({ error }) => this.swalService.apiError(error))
      ),
    { dispatch: false }
  );

  associateToDipendente$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.associateToDipendente),
      tap(() => this.loading.show()),
      switchMap(({ dto }) =>
        this.api.associateToDipendente(dto).pipe(
          map(resId =>
            GarageActions.associateToDipendenteSuccess({
              id: Number(resId ?? dto.idMezzo),
            })
          ),
          catchError(err =>
            of(GarageActions.associateToDipendenteFailure({
              error: err?.message ?? 'Errore associazione dipendente',
            }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  endDipendenteAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.endDipendenteAssociation),
      tap(() => this.loading.show()),
      switchMap(({ id, kmRitorno, dataRitorno }) =>
        this.api.endDipendenteAssociation(id, kmRitorno, dataRitorno).pipe(
          map(resId =>
            GarageActions.endDipendenteAssociationSuccess({
              id: Number(resId ?? id),
            })
          ),
          catchError(err =>
            of(GarageActions.endDipendenteAssociationFailure({
              error: err?.message ?? 'Errore termine associazione dipendente',
            }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  setUsability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GarageActions.setUsability),
      tap(() => this.loading.show()),
      switchMap(({ id, stato }) =>
        this.api.setUsability(id, stato).pipe(
          map(resId =>
            GarageActions.setUsabilitySuccess({
              id: Number(resId ?? id),
              stato,
            })
          ),
          catchError(err =>
            of(GarageActions.setUsabilityFailure({
              error: err?.message ?? 'Errore cambio stato mezzo',
            }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

}
